/**
 * Display-only amortised monthly payment helpers.
 *
 * Formula matches App\Services\Proposal\ProposalCalculator::amortisedMonthly
 * (effective monthly rate from a nominal APR). Do not invent a second
 * amortisation. Persisted / contract figures stay in Laravel
 * (ProposalContractMapper rounds the same float to 2 decimal places).
 *
 * Quote UI uses amortisedMonthly() which rounds to nearest whole pounds.
 */

export const DEFAULT_FINANCE_APR = 0.0999;
export const DEFAULT_FINANCE_TERM_MONTHS = 60;
export const MIN_FINANCE_TERM_MONTHS = 12;
export const MAX_FINANCE_TERM_MONTHS = 60;
/** Plan “Over how long” slider. Yearly steps up to 10 years. */
export const PLAN_MAX_FINANCE_TERM_MONTHS = 120;
export const FINANCE_TERM_STEP_MONTHS = 12;

/** Effective monthly rate from a nominal APR fraction (e.g. 0.0999). */
export function monthlyRateFromApr(apr: number): number {
  return (1 + apr) ** (1 / 12) - 1;
}

/**
 * Unrounded reducing-balance payment — same float as ProposalCalculator.
 * Prefer amortisedMonthly() for quote display.
 */
export function amortisedMonthlyExact(
  principal: number,
  apr: number = DEFAULT_FINANCE_APR,
  termMonths: number = DEFAULT_FINANCE_TERM_MONTHS
): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  if (apr <= 0) return principal / termMonths;

  const r = monthlyRateFromApr(apr);
  return (principal * r) / (1 - (1 + r) ** -termMonths);
}

/**
 * Standard reducing-balance amortised monthly payment for quote display.
 * Returns nearest whole pounds. Contracts round the exact float to 2dp.
 */
export function amortisedMonthly(
  principal: number,
  apr: number = DEFAULT_FINANCE_APR,
  termMonths: number = DEFAULT_FINANCE_TERM_MONTHS
): number {
  return Math.round(amortisedMonthlyExact(principal, apr, termMonths));
}

export function totalRepayable(monthlyPayment: number, termMonths: number): number {
  return Math.round(monthlyPayment * termMonths);
}

export function totalInterest(principal: number, monthlyPayment: number, termMonths: number): number {
  return Math.max(0, totalRepayable(monthlyPayment, termMonths) - Math.round(principal));
}

export function formatTermLabel(termMonths: number): string {
  const years = termMonths / 12;
  if (Number.isInteger(years)) {
    return years === 1 ? "1 year" : `${years} years`;
  }
  return `${termMonths} months`;
}

/** Finance form fields: loan_amount (GBP) + loan_term (months). */
export function buildFinanceApplyUrl(
  baseUrl: string,
  loanAmountGbp: number,
  termMonths: number = DEFAULT_FINANCE_TERM_MONTHS
): string {
  const trimmed = baseUrl.trim();
  if (!trimmed) return trimmed;

  try {
    const url = new URL(trimmed);
    if (loanAmountGbp > 0) {
      url.searchParams.set("loan_amount", String(Math.round(loanAmountGbp)));
    }
    const allowed = [12, 24, 36, 48, 60, 72, 84, 96, 108, 120];
    const term = allowed.includes(termMonths) ? termMonths : DEFAULT_FINANCE_TERM_MONTHS;
    url.searchParams.set("loan_term", String(term));
    return url.toString();
  } catch {
    return trimmed;
  }
}
